import { useState, type FC, type PropsWithChildren, useCallback } from 'react';

import { Button, type ButtonProps } from 'antd';
import { Link } from 'react-router-dom';
import { IconChevronRight } from '@tabler/icons-react';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';

interface Action {
  label: string
  onClick: () => void
  type: ButtonProps['type']
  link?: string
  danger?: boolean
  loading?: boolean
  disabled?: boolean
}

type Props = PropsWithChildren<{
  className?: string
  defaultCollpsed?: boolean
  actions?: Action[]
  description?: string
  flex?: number
  title?: string
}>;

const Box: FC<Props> = ({ className, defaultCollpsed, actions, children, description, flex = 1, title }) => {
  const [collapsed, setCollapsed] = useState(defaultCollpsed || false);
  const { theme } = useTheme();

  const toggleCollapsed = useCallback(() => setCollapsed((prev) => !prev), []);

  const isDark = theme === 'dark';
  const iconColor = isDark ? 'white' : '#1e242c';

  return (
    <div
      className={clsx(
        'mx-2 mt-2 mb-1 md:mb-2 p-3 md:p-5 relative overflow-auto rounded-lg border-2 flex flex-col shadow-sm hover:shadow-md transition-all duration-300',
        // Light mode styles
        'bg-white border-gray-200',
        // Dark mode styles
        'dark:bg-surface-dark dark:border-borders',
        // Custom className
        className
      )}
      style={{ flex, height: '100%' }}
    >
      {title && (
        <div className={clsx(
          'flex items-center sticky -top-0.5 z-10 mb-0 pb-3 transition-colors duration-300',
          // Light mode
          'bg-white',
          // Dark mode
          'dark:bg-surface-dark'
        )}>
          <button
            className={clsx(
              'border-none outline-none bg-transparent p-0 block md:hidden mr-2 transition-all duration-300 hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded',
              // Light mode focus
              'focus:ring-offset-white focus:ring-gray-400/20',
              // Dark mode focus
              'dark:focus:ring-offset-surface-dark dark:focus:ring-white/20'
            )}
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            <IconChevronRight
              size={13}
              color={iconColor}
              className={clsx('transition-transform duration-300', !collapsed && 'rotate-90')}
            />
          </button>
          <div className="flex-1">
            <h3 className={clsx(
              'font-semibold text-base md:text-lg transition-colors duration-300',
              'bg-gradient-to-r bg-clip-text',
              // Light mode
              'text-gray-900',
              // Dark mode
              'dark:text-white'
            )}>
              {title}
            </h3>
            <div className={clsx(
              'mt-1 h-0.5 w-12 rounded-full transition-colors duration-300',
              'bg-gradient-to-r from-blue-500 to-purple-500',
              'dark:from-blue-400 dark:to-purple-400'
            )} />
          </div>
        </div>
      )}
      <div className="flex overflow-auto flex-col flex-1">
        {description && (
          <div className={clsx(
            'flex items-center mt-2.5 transition-all duration-300',
            collapsed && 'hidden md:flex',
            // Light mode
            'text-gray-700',
            // Dark mode
            'dark:text-surface-light'
          )}>
            <p className="font-normal text-sm m-0">
              {description}
            </p>
          </div>
        )}
        <div className={clsx(
          'transition-all duration-300',
          collapsed && 'hidden md:block'
        )} style={{ height: '100%' }}>
          <div className={clsx('mb-2.5 flex-1 flex', className)} style={{ height: '100%' }}>
            {children}
          </div>
          {actions && actions.length > 0 && (
            <div className="mt-4 flex flex-col md:flex-row flex-wrap gap-2">
              {actions.map(({ label, link, type, onClick, danger, loading, disabled }, index) => {
                if (link) {
                  return (
                    <Link
                      to={link}
                      key={label}
                      className={clsx(index !== 0 && 'md:ml-0.5')}
                    >
                      <Button
                        type={type}
                        className="transition-all duration-200 hover:scale-105 active:scale-95 w-full md:w-auto"
                      >
                        {label}
                      </Button>
                    </Link>
                  );
                }
                return (
                  <Button
                    key={label}
                    type={type}
                    onClick={onClick}
                    danger={danger}
                    loading={loading}
                    disabled={disabled}
                    className="transition-all duration-200 hover:scale-105 active:scale-95 w-full md:w-auto"
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { Box };
